import React, { useState } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { AuthPageLayout } from '../components/auth/AuthPageLayout';
import { useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

const PasswordResetPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null);
  const [messageKey, setMessageKey] = useState<string>('');
  
  // Validation states
  const [emailError, setEmailError] = useState<string>('');
  const [newPasswordError, setNewPasswordError] = useState<string>('');
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>('');
  
  const { t } = useLanguage();
  const { resendVerificationEmail } = useAuth();

  // Check if we have a token (reset mode) or not (request mode)
  const isResetMode = !!token;

  // reCAPTCHA hook (only for request mode, not reset mode)
  const { recaptchaReady, captchaError, executeRecaptcha, isExecuting } = useRecaptcha({
    action: 'PASSWORD_RESET',
    onError: (error) => {
      setMessageType('error')
      setMessageKey('auth.captchaError')
    }
  })

  // Email validation
  const validateEmail = (email: string) => {
    if (!email) {
      setEmailError(t('auth.emailRequired'))
      return false
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setEmailError(t('auth.emailInvalid'))
      return false
    }
    setEmailError('')
    return true
  }

  // New password validation
  const validateNewPassword = (password: string) => {
    if (!password) {
      setNewPasswordError(t('auth.passwordRequired'))
      return false
    }
    if (password.length < 6) {
      setNewPasswordError(t('auth.passwordTooShort'))
      return false
    }
    setNewPasswordError('')
    return true
  }

  // Confirm password validation
  const validateConfirmPassword = (confirmPassword: string) => {
    if (!confirmPassword) {
      setConfirmPasswordError(t('auth.confirmPasswordRequired'))
      return false
    }
    if (newPassword !== confirmPassword) {
      setConfirmPasswordError(t('auth.passwordMismatch'))
      return false
    }
    setConfirmPasswordError('')
    return true
  }

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessageType(null);
    setMessageKey('');

    // Validate email
    const isEmailValid = validateEmail(email);
    if (!isEmailValid) {
      setIsLoading(false);
      return;
    }

    // Execute reCAPTCHA (only for request mode)
    if (!isResetMode) {
      if (!recaptchaReady) {
        setMessageType('error');
        setMessageKey('auth.captchaNotReady');
        setIsLoading(false);
        return;
      }

      try {
        const recaptchaToken = await executeRecaptcha();
        if (!recaptchaToken) {
          setMessageType('error');
          setMessageKey('auth.captchaError');
          setIsLoading(false);
          return;
        }

        const result = await authService.requestPasswordReset(email, recaptchaToken);

        setMessageType('success');
        setMessageKey('auth.resetEmailSent');
        setEmail('');
      } catch (error: any) {
        // Check if the error is due to unverified email
        if (error.message && error.message.includes('verify your email')) {
          setMessageType('error');
          setMessageKey('auth.passwordResetErrorUnverified');
        } else if (error.message?.includes('CaptchaFailed')) {
          setMessageType('error');
          setMessageKey('auth.captchaError');
        } else {
          setMessageType('error');
          setMessageKey('auth.resetEmailError');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessageType(null);
    setMessageKey('');

    // Validate all fields
    const isNewPasswordValid = validateNewPassword(newPassword);
    const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);

    if (!isNewPasswordValid || !isConfirmPasswordValid) {
      setIsLoading(false);
      return;
    }

    try {
      if (!token) {
        throw new Error('Invalid reset token');
      }
      const result = await authService.resetPassword(token, newPassword);

      setMessageType('success');
      setMessageKey('auth.passwordResetSuccess');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (error: any) {
      // Check if the error is due to unverified email
      if (error.message && error.message.includes('verify your email')) {
        setMessageType('error');
        setMessageKey('auth.passwordResetErrorUnverified');
      } else {
        setMessageType('error');
        setMessageKey('auth.passwordResetError');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle resend verification email
  const handleResendVerification = async () => {
    if (!email) return;
    
    setIsLoading(true);
    setMessageType(null);
    setMessageKey('');

    try {
      await resendVerificationEmail(email);
      setMessageType('success');
      setMessageKey('auth.verificationEmailSent');
    } catch (error: any) {
      setMessageType('error');
      setMessageKey('auth.verificationEmailError');
    } finally {
      setIsLoading(false);
    }
  };

  // Get the current message text based on the message key
  const getMessageText = () => {
    if (!messageType || !messageKey) return '';
    return t(messageKey);
  };

  return (
    <AuthPageLayout>
      <div className='mt-8'>
        <div className='text-center mb-8'>
          <h1 className='text-3xl font-bold text-gray-900 mb-2'>
            {isResetMode ? t('auth.resetPassword') : t('auth.passwordResetTitle')}
          </h1>
          <p className='text-gray-600 text-sm'>
            {isResetMode 
              ? t('auth.passwordResetSubtitle')
              : t('auth.passwordResetSubtitle')
            }
          </p>
        </div>

        {isResetMode ? (
          // Reset Password Form (with token)
          <form className='space-y-5' onSubmit={handleResetPassword}>
            <div>
              <label htmlFor='newPassword' className='block text-sm font-semibold text-gray-700 mb-2'>
                {t('auth.newPassword')}
              </label>
              <div className='relative'>
                <input
                  id='newPassword'
                  name='newPassword'
                  type={showNewPassword ? "text" : "password"}
                  autoComplete='new-password'
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value)
                    if (newPasswordError) validateNewPassword(e.target.value)
                  }}
                  onBlur={(e) => validateNewPassword(e.target.value)}
                  className={`w-full px-4 py-3 pr-12 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-gray-900 ${
                    newPasswordError ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder={t('auth.newPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  {showNewPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {newPasswordError && (
                <p className="mt-1 text-sm text-red-600">{newPasswordError}</p>
              )}
            </div>

            <div>
              <label htmlFor='confirmPassword' className='block text-sm font-semibold text-gray-700 mb-2'>
                {t('auth.confirmPassword')}
              </label>
              <div className='relative'>
                <input
                  id='confirmPassword'
                  name='confirmPassword'
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete='new-password'
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    if (confirmPasswordError) validateConfirmPassword(e.target.value)
                  }}
                  onBlur={(e) => validateConfirmPassword(e.target.value)}
                  className={`w-full px-4 py-3 pr-12 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-gray-900 ${
                    confirmPasswordError ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder={t('auth.confirmPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  {showConfirmPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {confirmPasswordError && (
                <p className="mt-1 text-sm text-red-600">{confirmPasswordError}</p>
              )}
            </div>

            {messageType && (
              <div className={`p-4 rounded-lg border ${
                messageType === 'success' 
                  ? 'bg-green-50 border-green-200 text-green-800' 
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                <div className='flex items-center'>
                  {messageType === 'success' ? (
                    <svg className='h-5 w-5 text-green-400 mr-2' fill='currentColor' viewBox='0 0 20 20'>
                      <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
                    </svg>
                  ) : (
                    <svg className='h-5 w-5 text-red-400 mr-2' fill='currentColor' viewBox='0 0 20 20'>
                      <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z' clipRule='evenodd' />
                    </svg>
                  )}
                  <span className='text-sm font-medium'>{getMessageText()}</span>
                </div>
              </div>
            )}

            {!isResetMode && captchaError && (
              <div className="p-4 rounded-lg border bg-red-50 border-red-200 text-red-800">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium">{captchaError}</span>
                </div>
              </div>
            )}

            <div className='pt-2'>
              <button
                type='submit'
                disabled={isLoading}
                className='w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200'
              >
                {isLoading ? (
                  <>
                    <svg className='animate-spin -ml-1 mr-3 h-5 w-5 text-white' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                      <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                      <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                    </svg>
                    {t('auth.resetting')}
                  </>
                ) : (
                  t('auth.resetPassword')
                )}
              </button>
            </div>
          </form>
        ) : (
          // Request Reset Form (no token)
          <form className='space-y-5' onSubmit={handleRequestReset}>
            <div>
              <label htmlFor='email' className='block text-sm font-semibold text-gray-700 mb-2'>
                {t('auth.email')}
              </label>
              <div className='relative'>
                <input
                  id='email'
                  name='email'
                  type='email'
                  autoComplete='email'
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    if (emailError) validateEmail(e.target.value)
                  }}
                  onBlur={(e) => validateEmail(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-gray-900 ${
                    emailError ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder='Enter your email'
                />
                <div className='absolute inset-y-0 right-0 flex items-center pr-3'>
                  <svg className='h-5 w-5 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207' />
                  </svg>
                </div>
              </div>
              {emailError && (
                <p className="mt-1 text-sm text-red-600">{emailError}</p>
              )}
            </div>

            {messageType && (
              <div className={`p-4 rounded-lg border ${
                messageType === 'success' 
                  ? 'bg-green-50 border-green-200 text-green-800' 
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                <div className='flex items-center'>
                  {messageType === 'success' ? (
                    <svg className='h-5 w-5 text-green-400 mr-2' fill='currentColor' viewBox='0 0 20 20'>
                      <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
                    </svg>
                  ) : (
                    <svg className='h-5 w-5 text-red-400 mr-2' fill='currentColor' viewBox='0 0 20 20'>
                      <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z' clipRule='evenodd' />
                    </svg>
                  )}
                  <span className='text-sm font-medium'>{getMessageText()}</span>
                </div>
                
                {/* Show resend verification button for unverified email errors */}
                {messageType === 'error' && messageKey === 'auth.passwordResetErrorUnverified' && email && (
                  <div className='mt-3 pt-3 border-t border-red-200'>
                    <button
                      type='button'
                      onClick={handleResendVerification}
                      disabled={isLoading}
                      className='w-full flex justify-center items-center py-2 px-4 border border-red-300 rounded-lg shadow-sm text-sm font-semibold text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200'
                    >
                      {isLoading ? (
                        <>
                          <svg className='animate-spin -ml-1 mr-2 h-4 w-4 text-red-700' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                            <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                            <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                          </svg>
                          {t('auth.loading')}
                        </>
                      ) : (
                        t('auth.resendVerificationEmail')
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className='pt-2'>
              <button
                type='submit'
                disabled={isLoading || isExecuting || (!isResetMode && !recaptchaReady)}
                className='w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200'
              >
                {isLoading || isExecuting ? (
                  <>
                    <svg className='animate-spin -ml-1 mr-3 h-5 w-5 text-white' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                      <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                      <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                    </svg>
                    {isExecuting ? 'Verifying...' : t('auth.loading')}
                  </>
                ) : (
                  t('auth.sendResetEmail')
                )}
              </button>
            </div>
          </form>
        )}

        <div className='mt-8 text-center'>
          <p className='text-sm text-gray-600'>
            <a href='/login' className='font-semibold text-green-700 transition-colors duration-200'>
              {t('auth.backToLogin')}
            </a>
          </p>
        </div>
      </div>
    </AuthPageLayout>
  );
};

export default PasswordResetPage; 